import { IgTypography } from "@intelligems/ig-theme";
import { AiPart } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { Message, MessageContent } from "@src/components/ai-elements/message";
import { SurveySummaryCard } from "@src/features/ai/components/WorkflowEditor/execution/turn/UserMessage/SurveySummaryCard";
import { cn } from "@src/lib/utils";
import { useExecutionPanelSlotProps } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { MessageFlags } from "@src/features/ai/components/WorkflowEditor/execution/types";

interface UserMessageProps {
  part: AiPart;
  timestamp?: string;
}

/**
 * Renders one text part of a user message. File parts are intercepted
 * upstream by `groupAndFilterNodes` into a `UserAttachmentGroup` so they
 * render as a single grid above the text bubble; file parts never reach
 * this component.
 */
export const UserMessage = ({
  part,
  timestamp,
  layoutDensity,
}: UserMessageProps & Pick<MessageFlags, "layoutDensity">) => {
  const theme = useTheme();
  const getSlotProps = useExecutionPanelSlotProps();
  const bubble = getSlotProps("userMessage");

  if (part.type !== "text") {
    return null;
  }

  // An answers reply covers several questions at once, so it reads back as the
  // "Survey summary" recap card rather than one run-on bubble. `text` is
  // untouched — the agent still receives the whole Q/A payload.
  const summary = part.surveySummary;

  return (
    <>
      <Message
        className={layoutDensity === "compact" ? "gap-1.5 py-2" : undefined}
        from={"user"}
      >
        {summary && summary.length > 0 ? (
          <SurveySummaryCard rows={summary} />
        ) : (
          <MessageContent
            className={cn(
              layoutDensity === "compact" && "max-w-[92%] gap-1 px-3 py-2",
              bubble?.className
            )}
          >
            {/*
            `displayText` is the render-only label a choice reply carries so
            the transcript shows what the chip said, not the raw option value
            the agent gets back. Absent on every other part.
          */}
            {part.displayText ?? part.text}
          </MessageContent>
        )}
      </Message>
      {timestamp && (
        <IgTypography
          variant="caption"
          sx={{
            color: theme.palette.text.tertiary,
            mt: 0.5,
            display: "block",
            textAlign: "right",
          }}
        >
          {timestamp}
        </IgTypography>
      )}
    </>
  );
};
